/**
 * Build-version drift detection.
 *
 * The API Worker emits `X-Build-Hash: <deploy-uuid>` on every response
 * (driven by Cloudflare's `version_metadata` binding). The SPA's fetch
 * wrapper feeds every observed hash into `observeBuildHash` here; we
 * latch the first non-empty value, then watch for subsequent values to
 * differ. When they do, the page is running against a newer API than
 * the bundle that loaded it — we render a banner prompting the user to
 * refresh before stale-bundle requests start failing with
 * NS_BINDING_ABORTED-style hard-cancels.
 *
 * `useBuildDrift()` is the React subscription. Pure plain-React (no
 * external state lib) since we only fan one boolean across the tree.
 */
import { useSyncExternalStore } from 'react';

let firstSeen: string | null = null;
let current: string | null = null;
const subscribers = new Set<() => void>();

function notify() {
  for (const cb of subscribers) cb();
}

export function observeBuildHash(hash: string | null | undefined): void {
  if (!hash) return;
  if (firstSeen == null) {
    firstSeen = hash;
    current = hash;
    return;
  }
  if (hash !== current) {
    current = hash;
    notify();
  }
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

function getSnapshot(): boolean {
  return firstSeen != null && current != null && firstSeen !== current;
}

// SSR pass: no drift before hydration. The Vite SPA doesn't actually
// SSR, but `useSyncExternalStore` insists on a server snapshot for
// React 18 hydration safety.
function getServerSnapshot(): boolean {
  return false;
}

export function useBuildDrift(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Exposed only so tests / debug tooling can reset module state between
 * cases. Production code never calls this.
 */
export function _resetBuildVersionState(): void {
  firstSeen = null;
  current = null;
  subscribers.clear();
}
